import useSWRBase, { SWRConfiguration } from 'swr';

import { DEFAULT_OPTIONS } from '@/constants/swrDefaultOptions';

import { FetcherParams, fetcher } from '../data/fetcher';
import { InfoResponse } from '../types/schema';
import { apiInfoPath } from '../utils/constants';

export enum InstanceFlags {
  org_creation_disabled = 'org_creation_disabled',
  show_ttl_ui = 'show_ttl_ui',
  trace_tier_duration_days = 'trace_tier_duration_days',
  search_enabled = 'search_enabled',
  generate_ai_query_enabled = 'generate_ai_query_enabled',
  workspace_scope_org_invites = 'workspace_scope_org_invites',
  personal_orgs_disabled = 'personal_orgs_disabled',
  payment_enabled = 'payment_enabled',
  experimental_search_enabled = 'experimental_search_enabled',
  playground_auth_bypass_enabled = 'playground_auth_bypass_enabled',
  blob_storage_enabled = 'blob_storage_enabled',
  self_hosted_jit_provisioning_enabled = 'self_hosted_jit_provisioning_enabled',
}

const DEFAULT_INSTANCE_FLAGS = {
  [InstanceFlags.org_creation_disabled]: false,
  [InstanceFlags.show_ttl_ui]: false,
  [InstanceFlags.trace_tier_duration_days]: {
    shortlived: 14,
    longlived: 400,
  },
  [InstanceFlags.workspace_scope_org_invites]: false,
  [InstanceFlags.search_enabled]: true,
  [InstanceFlags.generate_ai_query_enabled]: false,
  [InstanceFlags.personal_orgs_disabled]: false,
  [InstanceFlags.payment_enabled]: false,
  [InstanceFlags.experimental_search_enabled]: false,
  [InstanceFlags.playground_auth_bypass_enabled]: false,
  [InstanceFlags.blob_storage_enabled]: false,
  [InstanceFlags.self_hosted_jit_provisioning_enabled]: true,
};

type InstanceFlagType<Flag extends InstanceFlags> =
  (typeof DEFAULT_INSTANCE_FLAGS)[Flag];

export const useInfo = (options?: SWRConfiguration & { skip?: boolean }) => {
  return useSWRBase<InfoResponse, Error, FetcherParams | null>(
    options?.skip ? null : { url: apiInfoPath, method: 'GET' },
    fetcher,
    { ...DEFAULT_OPTIONS, ...options }
  );
};
export function useInstanceFlag<Flag extends InstanceFlags>(
  flag: Flag
): null | InstanceFlagType<Flag> {
  const { data: info, isLoading } = useInfo();

  if (isLoading) {
    return null;
  }

  const flagValue = info?.instance_flags?.[flag];
  if (flagValue !== undefined) {
    return flagValue as InstanceFlagType<Flag>;
  }

  return DEFAULT_INSTANCE_FLAGS[flag] ?? null;
}

export function useInstanceFlagOrDefault<Flag extends InstanceFlags>(
  flag: Flag
): InstanceFlagType<Flag> {
  const flagValue = useInstanceFlag(flag);
  return flagValue ?? DEFAULT_INSTANCE_FLAGS[flag];
}
